import { useTranslation } from "react-i18next";
import styles from "./style.module.scss";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { IOrganizations } from "../../../../../setup/types/Organisation";
import Spinner from "../../../../../components/common/Spinner";
import classNames from "classnames";
import {
  getOrganizationTiers,
  getPaymentTypes,
  addBulkOrganizationPaymentTypeFee,
  updateOrganizationPaymentTypeFees,
  updateFeeTemplate,
} from "../../../../../setup/api/services/organization";
import {
  IOrganizationFeeTier,
  IOrganizationFeeType,
  IOrganizationPaymentBrand,
  IOrganizationPaymentFee,
  IBaseApiResponse,
} from "../../../../../setup/api/services/organization/models";
import {
  EPaymentBrand,
  EPaymentType,
} from "../../../../../setup/api/services/contract/models";
import { matchPath, useLocation } from "react-router-dom";
import { ROUTES } from "../../../../../setup/constants";
import { RowWithOptionalClick } from "../../../../../components/table";
import TextInput, {
  InputType,
} from "../../../../../components/forms/textInput/textInput";
import PrimaryButton, {
  PrimaryButtonType,
  PrimaryButtonSize,
} from "../../../../../components/common/PrimaryButton/PrimaryButton";
import OutlinedButton, {
  OutlinedButtonSize,
} from "../../../../../components/common/OutlinedButton";

interface TiersInformationProps {
  organization: IOrganizations | undefined;
  isEditable?: boolean;
  onTiersChange?: (tiers: TierInfo[]) => void;
  onSave?: (tiers: TierInfo[]) => void;
  /** Rendered next to the Save button so a caller-provided action (e.g. "Close")
   * lines up on the same row instead of the caller adding its own button row below. */
  secondaryAction?: React.ReactNode;
}

// Define tier information structure
interface TierInfo extends RowWithOptionalClick {
  id?: number;
  name: string;
  from: number | string;
  to: number | string;
  bankTransfer: string;
  visa: string;
  mastercard: string;
  amex: string;
  mada: string;
  tabby: string;
  neo: string;
  tamara: string;
  rawPaymentTypes?: IOrganizationPaymentFee[]; // Store original payment types for ID tracking
}

// The eight payment brands that carry a per-tier fee, in the order they appear as table
// columns. Single source of truth for the matrix columns, the editor fields and the save
// payload, so the three can't drift apart.
const FEE_COLUMNS: {
  field: keyof TierInfo;
  labelKey: string;
  brand: IOrganizationPaymentBrand;
  /** Cards are charged as a percentage, everything else as a flat SAR amount. Used to pick
   * the unit for a blank field, since an empty value carries no unit of its own. */
  isPercent: boolean;
}[] = [
  {
    field: "bankTransfer",
    labelKey: "lbl_bank_transfer_amount",
    brand: "BANK_TRANSFER",
    isPercent: false,
  },
  { field: "visa", labelKey: "lbl_visa", brand: "VISA", isPercent: true },
  {
    field: "mastercard",
    labelKey: "lbl_master_card",
    brand: "MASTER",
    isPercent: true,
  },
  { field: "amex", labelKey: "lbl_amex", brand: "AMEX", isPercent: true },
  { field: "mada", labelKey: "lbl_mada", brand: "MADA", isPercent: true },
  {
    field: "tabby",
    labelKey: "lbl_tabby_amount",
    brand: "TABBY",
    isPercent: false,
  },
  { field: "neo", labelKey: "lbl_neo_amount", brand: "NEO", isPercent: false },
  {
    field: "tamara",
    labelKey: "lbl_tamara_amount",
    brand: "TAMARA",
    isPercent: false,
  },
];

// Which backend payment type (gateway) each fee brand is charged through, most preferred
// first. Only the gateway *names* are stable - their ids are database ids that differ per
// environment and aren't contiguous, so they're resolved at runtime from
// GET /api/payment-types. Sending a guessed id violates the
// organization_payment_types_payment_type_id_fkey constraint and the save fails with a 500.
const BRAND_GATEWAYS: Record<IOrganizationPaymentBrand, EPaymentType[]> = {
  BANK_TRANSFER: [EPaymentType.MANUAL_BANK_TRANSFER],
  // Card brands aren't payment types of their own - they're charged through the card gateway.
  VISA: [EPaymentType.MOYASAR, EPaymentType.HYPER_PAY],
  MASTER: [EPaymentType.MOYASAR, EPaymentType.HYPER_PAY],
  AMEX: [EPaymentType.MOYASAR, EPaymentType.HYPER_PAY],
  MADA: [EPaymentType.MOYASAR, EPaymentType.HYPER_PAY],
  APPLE_PAY: [EPaymentType.MOYASAR, EPaymentType.HYPER_PAY],
  STCPAY: [EPaymentType.STCPAY],
  TABBY: [EPaymentType.TABBY],
  NEO: [EPaymentType.NEO],
  TAMARA: [EPaymentType.TAMARA],
};

const TiersInformation = ({
  organization,
  isEditable,
  onTiersChange,
  onSave,
  secondaryAction,
}: TiersInformationProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [tiers, setTiers] = useState<TierInfo[]>([]);
  const [initialTiers, setInitialTiers] = useState<TierInfo[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [templateId, setTemplateId] = useState<number | undefined>();
  const [templateMetadata, setTemplateMetadata] = useState<{
    name: string;
    description: string;
    active: boolean;
  } | null>(null);
  const [initialMetadata, setInitialMetadata] = useState<{
    name: string;
    description: string;
    active: boolean;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  // Editing happens in a panel under the table rather than inside the cells: eleven fee
  // columns leave no room for a usable input, so only one tier is expanded at a time.
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const editorRef = useRef<HTMLDivElement | null>(null);

  // The editor is attached below the table, so with several tiers it can open off-screen.
  useEffect(() => {
    if (expandedRow === null) return;
    editorRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [expandedRow]);

  // brand -> payment type id, resolved from the backend rather than hardcoded. Held in a ref
  // because only handleSave reads it and a late arrival must not re-render the editor.
  const paymentTypeIdsRef = useRef<Partial<
    Record<IOrganizationPaymentBrand, number>
  > | null>(null);
  // brand -> payment type id as reported by the fee template itself. The template response
  // carries a row per brand (even before a fee is set), which is the most authoritative
  // mapping there is; it just doesn't necessarily cover every brand.
  const templatePaymentTypeIdsRef = useRef<
    Partial<Record<IOrganizationPaymentBrand, number>>
  >({});

  /** Build the brand -> payment type id map from the payment types the backend reports.
   * Never rejects: the call can be denied (the endpoint isn't open to every admin role yet),
   * and that must not block saving fees whose ids are already known from the fee template. */
  const resolvePaymentTypeIds = async () => {
    if (paymentTypeIdsRef.current) return paymentTypeIdsRef.current;

    const res = await getPaymentTypes().catch((error) => {
      console.error("Failed to load payment types", error);
      return null;
    });

    // Not cached on failure, so the next save retries rather than being stuck with an empty map.
    if (!res?.data)
      return {} as Partial<Record<IOrganizationPaymentBrand, number>>;

    const idByType = new Map(
      res.data.map((paymentType) => [paymentType.type, paymentType.id])
    );

    const map: Partial<Record<IOrganizationPaymentBrand, number>> = {};
    (Object.keys(BRAND_GATEWAYS) as IOrganizationPaymentBrand[]).forEach(
      (brand) => {
        const gateway = BRAND_GATEWAYS[brand].find((type) =>
          idByType.has(type)
        );
        if (gateway) map[brand] = idByType.get(gateway);
      }
    );

    paymentTypeIdsRef.current = map;
    return map;
  };

  const isCreateMatch = matchPath(
    { path: ROUTES.CREATE_COMPLEX_CONTRACT },
    location.pathname
  );

  useEffect(() => {
    const fetchOrganizationTiers = async () => {
      if (!organization?.code || hasFetched) return;
      setIsLoading(true);
      try {
        const tiersRes = await getOrganizationTiers(organization?.code);

        // Warm the gateway id lookup so saving doesn't have to wait on it. A failure here is
        // not fatal - handleSave retries the call before it needs the ids.
        resolvePaymentTypeIds();

        const templateIds: Partial<Record<IOrganizationPaymentBrand, number>> =
          {};
        tiersRes?.data?.tiers?.forEach((tier: IOrganizationFeeTier) =>
          tier?.paymentTypes?.forEach((pt: IOrganizationPaymentFee) => {
            if (pt?.brand && pt?.paymentTypeId)
              templateIds[pt.brand] = pt.paymentTypeId;
          })
        );
        templatePaymentTypeIdsRef.current = templateIds;

        setTemplateId(tiersRes?.data?.id);
        if (tiersRes?.data) {
          const metadata = {
            name: tiersRes.data.name,
            description: tiersRes.data.description,
            active: tiersRes.data.active,
          };
          setTemplateMetadata(metadata);
          setInitialMetadata(metadata);
        }

        const getFeeDisplay = (
          tier: IOrganizationFeeTier,
          brand: EPaymentBrand
        ) => {
          const pt = tier?.paymentTypes?.find(
            (p: IOrganizationPaymentFee) => p.brand === brand
          );
          // "-" means no fee has been set for this brand yet - not the same as an
          // explicit 0. Left blank so an unfilled field is never mistaken for (or
          // saved as) a real zero fee; see the numValue check in handleSave.
          if (pt === undefined || pt.fees === null || pt.fees === undefined)
            return "-";

          if (pt.feeType === "PERCENTAGE") {
            return `${(pt.fees * 100).toFixed(1)}%`;
          }
          return `${pt.fees} ${t("lbl_saudi_currency")}`;
        };

        // A tier with no payment type fees configured yet (e.g. the placeholder default
        // tier auto-created alongside a newly-verified organization's fee template) - open
        // it directly in edit mode so the admin can fill in real values without an extra click.
        const isTierUnconfigured = (tier: IOrganizationFeeTier) =>
          !tier?.paymentTypes?.some(
            (pt) => pt.fees !== null && pt.fees !== undefined
          );

        const mappedTiers: TierInfo[] =
          tiersRes?.data?.tiers?.map((tier: IOrganizationFeeTier) => ({
            id: tier?.id,
            name: tier?.name,
            from: tier?.from,
            to: tier?.to || "-",
            rawPaymentTypes: tier?.paymentTypes,
            bankTransfer: getFeeDisplay(tier, EPaymentBrand.BANK_TRANSFER),
            visa: getFeeDisplay(tier, EPaymentBrand.VISA),
            mastercard: getFeeDisplay(tier, EPaymentBrand.MASTER),
            amex: getFeeDisplay(tier, EPaymentBrand.AMEX),
            mada: getFeeDisplay(tier, EPaymentBrand.MADA),
            tabby: getFeeDisplay(tier, EPaymentBrand.TABBY),
            neo: getFeeDisplay(tier, EPaymentBrand.NEO),
            tamara: getFeeDisplay(tier, EPaymentBrand.TAMARA),
          })) || [];

        setTiers(mappedTiers);
        setInitialTiers(JSON.parse(JSON.stringify(mappedTiers)));
        setHasUnsavedChanges(false);
        setHasFetched(true);

        if (isEditable) {
          const firstUnconfigured = tiersRes?.data?.tiers?.findIndex(
            (tier: IOrganizationFeeTier) => isTierUnconfigured(tier)
          );
          setExpandedRow(
            firstUnconfigured === undefined || firstUnconfigured < 0
              ? null
              : firstUnconfigured
          );
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrganizationTiers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization?.code, t]);

  const handleFieldChange = (
    rowIndex: number,
    field: keyof TierInfo,
    value: string | number
  ) => {
    const updatedTiers = [...tiers];
    let finalValue = value;

    if (field === "from") {
      const fromVal = Number(value) || 0;
      const toVal = updatedTiers[rowIndex].to;
      if (toVal !== "-" && Number(toVal) < fromVal + 1) {
        updatedTiers[rowIndex].to = fromVal + 1;
      }
    }

    if (field === "to") {
      const fromValue = Number(updatedTiers[rowIndex].from) || 0;
      if (value === "" || value === "-") {
        finalValue = "-";
      } else {
        const strVal = value.toString();
        const numTo = Number(strVal);

        if (strVal.endsWith(".")) {
          finalValue = strVal;
        } else if (numTo === 0) {
          finalValue = "-";
        } else if (!isNaN(numTo) && numTo < fromValue + 1) {
          finalValue = fromValue + 1;
        } else {
          finalValue = strVal;
        }
      }
    }

    updatedTiers[rowIndex] = {
      ...updatedTiers[rowIndex],
      [field]: finalValue,
    };
    setTiers(updatedTiers);
    onTiersChange?.(updatedTiers);
    setHasUnsavedChanges(true);
  };

  const handleAddTier = () => {
    const newTier: TierInfo = {
      name: "",
      from: 0,
      to: "-",
      bankTransfer: "-",
      visa: "-",
      mastercard: "-",
      amex: "-",
      mada: "-",
      tabby: "-",
      neo: "-",
      tamara: "-",
    };
    const updatedTiers = [...tiers, newTier];
    const newIndex = updatedTiers.length - 1;

    setTiers(updatedTiers);
    onTiersChange?.(updatedTiers);
    setExpandedRow(newIndex);
    setHasUnsavedChanges(true);
  };

  const handleToggleExpand = (rowIndex: number) => {
    setExpandedRow((prev) => (prev === rowIndex ? null : rowIndex));
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedTiers = tiers.filter((_, index) => index !== rowIndex);
    setTiers(updatedTiers);
    onTiersChange?.(updatedTiers);

    // Keep the open editor pointed at the same tier now that the rows below shifted up.
    setExpandedRow((prev) => {
      if (prev === null || prev === rowIndex) return null;
      return prev > rowIndex ? prev - 1 : prev;
    });
    setHasUnsavedChanges(true);
  };

  /** Discard the in-progress edits for one tier: restore its last saved values, or drop the
   * row entirely when it was never saved. */
  const handleCancelEdit = (rowIndex: number) => {
    const row = tiers[rowIndex];
    const original = row?.id
      ? initialTiers.find((it) => it.id === row.id)
      : undefined;

    if (!original) {
      // A draft row has nothing to restore - remove it, unless it is the only tier left.
      if (tiers.length > 1) handleDeleteRow(rowIndex);
      else setExpandedRow(null);
      return;
    }

    const updatedTiers = [...tiers];
    updatedTiers[rowIndex] = JSON.parse(JSON.stringify(original));
    setTiers(updatedTiers);
    onTiersChange?.(updatedTiers);
    setExpandedRow(null);
  };

  /** Fee values carry their unit in the stored string ("3.5%" / "5 ﷼"), so switching unit
   * rewrites the value while keeping whatever number is already typed. `currentUnit` is the
   * unit the field is showing, which for a blank field is a fallback rather than something
   * the stored value carries - comparing against it keeps re-picking it from dirtying the form. */
  const handleSetUnit = (
    rowIndex: number,
    field: keyof TierInfo,
    currentUnit: string,
    nextUnit: string
  ) => {
    if (currentUnit === nextUnit) return;

    const fieldValue = tiers[rowIndex]?.[field];
    if (typeof fieldValue === "function") return;

    const { numValue } = parseValue(fieldValue as string | number);
    const updatedTiers = [...tiers];
    updatedTiers[rowIndex] = {
      ...updatedTiers[rowIndex],
      [field]: composeFeeValue(numValue, nextUnit),
    };
    setTiers(updatedTiers);
    onTiersChange?.(updatedTiers);
    setHasUnsavedChanges(true);
  };

  const isDirty = useMemo(() => {
    if (!hasUnsavedChanges) return false;

    const tiersChanged = JSON.stringify(tiers) !== JSON.stringify(initialTiers);
    const metadataChanged =
      templateMetadata?.name !== initialMetadata?.name ||
      templateMetadata?.description !== initialMetadata?.description ||
      templateMetadata?.active !== initialMetadata?.active;

    return tiersChanged || metadataChanged;
  }, [
    tiers,
    initialTiers,
    hasUnsavedChanges,
    templateMetadata,
    initialMetadata,
  ]);

  const handleSave = async () => {
    if (!organization || !isDirty) return;

    try {
      setIsLoading(true);

      // Fee records are rejected by the backend unless paymentTypeId is a real payment type
      // id, so get the mapping before anything is written.
      const gatewayIds = await resolvePaymentTypeIds();

      // Checked before anything is written: a brand with no gateway id can't have its fee
      // saved, and going ahead would leave the template updated with the fees silently
      // dropped. Throwing here leaves the form dirty so the edits survive a retry. Only the
      // brands actually edited are checked - an unmapped brand nobody touched sends nothing
      // and so can't block the save.
      const unmappedBrands = FEE_COLUMNS.filter(({ field, brand }) => {
        if (templatePaymentTypeIdsRef.current[brand] || gatewayIds[brand])
          return false;

        return tiers.some((localTier) => {
          const initialTier = initialTiers.find((it) => it.id === localTier.id);
          const currentVal = localTier[field] as string;
          const initialVal = initialTier
            ? (initialTier[field] as string)
            : null;
          // Same conditions the payload loop below skips on.
          return (
            currentVal !== initialVal && parseValue(currentVal).numValue !== ""
          );
        });
      }).map(({ brand }) => brand);

      if (unmappedBrands.length > 0) {
        throw new Error(
          `No payment type id for ${unmappedBrands.join(
            ", "
          )} - nothing saved. ` +
            `These brands have no fee row on the template yet, so their gateway id can only ` +
            `come from GET /api/payment-types; check that call succeeded.`
        );
      }

      // 1. Check if template metadata or tiers (count/name/range) changed
      const currentTemplateTiers = tiers.map((tier) => ({
        id: tier.id,
        name: tier.name,
        from: Number(tier.from) || 0,
        to: tier.to === "-" ? null : Number(tier.to) || 0,
      }));

      const initialTemplateTiers = initialTiers.map((tier) => ({
        id: tier.id,
        name: tier.name,
        from: Number(tier.from) || 0,
        to: tier.to === "-" ? null : Number(tier.to) || 0,
      }));

      // Detect any change in metadata OR tiers (including count/deletions/renames)
      const templateMetadataChanged =
        JSON.stringify(currentTemplateTiers) !==
          JSON.stringify(initialTemplateTiers) ||
        templateMetadata?.name !== initialMetadata?.name ||
        templateMetadata?.description !== initialMetadata?.description ||
        templateMetadata?.active !== initialMetadata?.active;

      let updatedServerTiers = initialTiers.map((t) => ({
        id: t.id as number,
        name: t.name,
        from: Number(t.from) || 0,
        to: t.to === "-" ? null : (Number(t.to) as number),
        paymentTypes: t.rawPaymentTypes || [],
      }));

      if (templateMetadataChanged && templateId) {
        const templateRes = await updateFeeTemplate(Number(templateId), {
          name: templateMetadata?.name || "",
          description: templateMetadata?.description || "",
          active: templateMetadata?.active ?? true,
          tiers: currentTemplateTiers as IOrganizationFeeTier[],
        });
        updatedServerTiers = templateRes?.data?.tiers || [];
      }

      const activeTemplateId = templateId;

      // 2. Identify and collect payment type fees (New vs Existing)
      const feesToSync: IOrganizationPaymentFee[] = [];
      const feesToAdd: IOrganizationPaymentFee[] = [];

      tiers.forEach((localTier) => {
        // 1. Only process tiers that have actual changes
        const initialTier = initialTiers.find((it) => it.id === localTier.id);
        const isTierModified =
          !initialTier ||
          JSON.stringify(localTier) !== JSON.stringify(initialTier);

        if (!isTierModified) return;

        // 2. Match with the newly updated server tier to get the correct tierId
        const serverTier = localTier.id
          ? updatedServerTiers.find((st) => st.id === localTier.id)
          : updatedServerTiers.find(
              (st) => Number(st.from) === Number(localTier.from)
            );

        const tierId = serverTier?.id;
        if (!tierId) return;

        FEE_COLUMNS.forEach(({ field, brand }) => {
          const currentVal = localTier[field] as string;
          const initialVal = initialTier
            ? (initialTier[field] as string)
            : null;

          if (currentVal === initialVal) return;

          const { numValue, unit } = parseValue(currentVal);
          // Nothing entered for this brand - don't invent a 0 fee and send it. Number("")
          // is 0, not NaN, so this has to be checked before it reaches the fees calc below.
          if (numValue === "") return;

          // Check if this payment type already existed to get its ID
          const existingFeeRecord = localTier.rawPaymentTypes?.find(
            (pt) => pt.brand === brand
          );

          // Most specific source first: the id the backend already stored for this exact fee
          // row, then the one it uses for this brand elsewhere in the template, then the
          // brand's gateway from /api/payment-types.
          const paymentTypeId =
            existingFeeRecord?.paymentTypeId ||
            templatePaymentTypeIdsRef.current[brand] ||
            gatewayIds[brand];

          // Unreachable after the pre-flight check above; kept so the payload can never carry
          // an undefined id.
          if (!paymentTypeId) return;

          const feeObj: IOrganizationPaymentFee = {
            ...(existingFeeRecord?.id ? { id: existingFeeRecord.id } : {}),
            feeTemplateId: Number(activeTemplateId),
            feeTemplateTierId: tierId,
            paymentTypeId: paymentTypeId,
            fees:
              unit === "%"
                ? (Number(numValue) || 0) / 100
                : Number(numValue) || 0,
            feeType: (unit === "%"
              ? "PERCENTAGE"
              : "FLAT") as IOrganizationFeeType,
            brand,
          };

          if (existingFeeRecord?.id) {
            feesToSync.push(feeObj);
          } else {
            feesToAdd.push(feeObj);
          }
        });
      });

      // 3. Save fees: POST for new records, PUT for existing records (Full-state synchronization)
      const promises: Promise<IBaseApiResponse>[] = [];

      if (feesToAdd.length > 0) {
        promises.push(
          addBulkOrganizationPaymentTypeFee(organization.code, feesToAdd)
        );
      }

      if (feesToSync.length > 0) {
        promises.push(
          updateOrganizationPaymentTypeFees(organization.code, {
            paymentTypes: feesToSync,
          })
        );
      }

      if (promises.length > 0) {
        await Promise.all(promises);
      }

      // Reset dirty state explicitly
      setHasUnsavedChanges(false);
      setInitialTiers(JSON.parse(JSON.stringify(tiers)));
      if (templateMetadata) setInitialMetadata({ ...templateMetadata });

      // Refresh tiers to get updated IDs and data from server
      setHasFetched(false);
      setExpandedRow(null);
      onSave?.(tiers);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const parseValue = (value: string | number) => {
    if (typeof value === "number")
      return { numValue: value.toString(), unit: "" };

    const valStr = value?.toString().trim() || "";
    if (valStr === "" || valStr === "-") return { numValue: "", unit: "" };

    // Remove commas from numbers like "100,000"
    const cleanValue = valStr.replace(/,/g, "");
    const match = cleanValue.match(/^([\d.]*)\s*(.*)$/);
    if (match) {
      return {
        numValue: match[1],
        unit: match[2],
      };
    }
    return { numValue: "", unit: "" };
  };

  /** A fee keeps its unit inside the stored string ("3.5%" / "5 ﷼"). Composed in one place so
   * the input, the unit switch and the save mapping can never disagree about the format. */
  const composeFeeValue = (numValue: string | number, unit: string) =>
    unit === "%" ? `${numValue}%` : `${numValue} ${t("lbl_saudi_currency")}`;

  /** The unit to show for a fee field. A blank value carries no unit of its own, so fall back
   * to what the brand itself takes (percentage for cards, flat SAR for everything else). */
  const feeUnitOf = (value: string | number, isPercent: boolean) =>
    parseValue(value).unit || (isPercent ? "%" : t("lbl_saudi_currency"));

  if (isLoading) {
    return (
      <div className={styles.spinnerContainer}>
        <Spinner />
      </div>
    );
  }

  // Numbers are always shown LTR inside the RTL table, with the unit as a separate muted
  // token - no bidi guessing, and "5 ﷼" can't be reordered into "﷼ 5".
  const renderFeeValue = (value: string | number) => {
    const { numValue, unit } = parseValue(value);
    if (numValue === "") return <span className={styles.emptyValue}>—</span>;

    return (
      <span className={styles.feeValue} dir="ltr">
        <span className={styles.feeNumber}>{numValue}</span>
        <span className={styles.feeUnit}>{unit}</span>
      </span>
    );
  };

  const renderBound = (value: number | string, isUpperBound?: boolean) => {
    if (value === null || value === undefined || value === "" || value === "-")
      return <span className={styles.emptyValue}>—</span>;

    const numeric = Number(value);
    return (
      <span
        className={classNames(styles.boundValue, {
          [styles.upperBound]: isUpperBound,
        })}
        dir="ltr">
        {isNaN(numeric) ? String(value) : numeric.toLocaleString()}
      </span>
    );
  };

  const inputStyle: React.CSSProperties = {
    textAlign: "right",
    fontSize: "15px",
    padding: 0,
    minWidth: "unset",
  };

  const renderEditor = (rowIndex: number) => {
    const row = tiers[rowIndex];
    if (!row) return null;

    return (
      <div className={styles.editorPanel} ref={editorRef}>
        <div className={styles.editorHeader}>
          <h4 className={styles.editorTitle}>
            {t("lbl_edit_tier_fees")}
            <span className={styles.editorTierName}>
              {row.name?.trim() || t("lbl_new_tier")}
            </span>
          </h4>
          <p className={styles.editorHint}>{t("lbl_tier_fee_unit_hint")}</p>
        </div>

        <div className={styles.editorGrid}>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>{t("lbl_tier_name")}</span>
            <div className={styles.fieldControl}>
              <TextInput
                type={InputType.DEFAULT}
                value={row.name}
                placeholder={t("lbl_tier_name")}
                containerWidth="100%"
                containerHeight="44px"
                style={inputStyle}
                handleChange={(e) =>
                  handleFieldChange(rowIndex, "name", e.target.value)
                }
              />
            </div>
          </div>

          <div className={styles.field}>
            <span className={styles.fieldLabel}>{t("lbl_from")}</span>
            <div className={styles.fieldControl}>
              <TextInput
                type={InputType.NUMBER}
                value={row.from === "-" ? "" : String(row.from ?? "")}
                placeholder="0"
                maxDecimals={1}
                containerWidth="100%"
                containerHeight="44px"
                style={inputStyle}
                handleChange={(e) =>
                  handleFieldChange(rowIndex, "from", e.target.value)
                }
              />
            </div>
          </div>

          <div className={styles.field}>
            <span className={styles.fieldLabel}>{t("lbl_to")}</span>
            <div className={styles.fieldControl}>
              <TextInput
                type={InputType.NUMBER}
                value={row.to === "-" ? "" : String(row.to ?? "")}
                placeholder={t("lbl_no_upper_limit")}
                maxDecimals={1}
                containerWidth="100%"
                containerHeight="44px"
                style={inputStyle}
                handleChange={(e) =>
                  handleFieldChange(rowIndex, "to", e.target.value)
                }
              />
            </div>
          </div>
        </div>

        <div className={styles.editorDivider} />

        <div className={styles.editorGrid}>
          {FEE_COLUMNS.map(({ field, labelKey, isPercent }) => {
            const value = row[field] as string;
            const { numValue } = parseValue(value);
            const unit = feeUnitOf(value, isPercent);
            const isPercentSelected = unit === "%";

            return (
              <div className={styles.field} key={field}>
                <span className={styles.fieldLabel}>{t(labelKey)}</span>
                <div className={styles.fieldControl}>
                  <TextInput
                    type={InputType.NUMBER}
                    value={numValue}
                    placeholder="0"
                    maxValue={isPercentSelected ? 100 : undefined}
                    maxDecimals={1}
                    containerWidth="100%"
                    containerHeight="44px"
                    style={inputStyle}
                    handleChange={(e) =>
                      handleFieldChange(
                        rowIndex,
                        field,
                        composeFeeValue(e.target.value, unit)
                      )
                    }
                  />
                  <div
                    className={styles.unitSwitch}
                    role="group"
                    aria-label={t("lbl_fee_unit")}>
                    <button
                      type="button"
                      aria-pressed={!isPercentSelected}
                      className={classNames(styles.unitOption, {
                        [styles.unitOptionActive]: !isPercentSelected,
                      })}
                      onClick={() =>
                        handleSetUnit(
                          rowIndex,
                          field,
                          unit,
                          t("lbl_saudi_currency")
                        )
                      }>
                      {t("lbl_saudi_currency")}
                    </button>
                    <button
                      type="button"
                      aria-pressed={isPercentSelected}
                      className={classNames(styles.unitOption, {
                        [styles.unitOptionActive]: isPercentSelected,
                      })}
                      onClick={() => handleSetUnit(rowIndex, field, unit, "%")}>
                      %
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className={styles.editorActions}>
          <PrimaryButton
            buttonType={PrimaryButtonType.PRIMARY_DARK}
            size={PrimaryButtonSize.MEDIUM}
            width="160px"
            disabled={!isDirty}
            onClick={handleSave}>
            {t("btn_save_changes")}
          </PrimaryButton>
          <OutlinedButton
            size={OutlinedButtonSize.MEDIUM}
            onClick={() => handleCancelEdit(rowIndex)}>
            {t("lbl_cancel")}
          </OutlinedButton>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerContainer}>
        <div className={styles.titleWrapper}>
          <div className={styles.textWrapper}>
            <h3
              className={styles.title}
              style={{ fontSize: isCreateMatch ? "24px" : "18px" }}>
              {isCreateMatch
                ? t("lbl_payment_methods_fees")
                : t("lbl_tiers_info_title")}
            </h3>
            <p className={styles.subtitle}>{t("lbl_tiers_info_subtitle")}</p>
          </div>
          {isEditable && (
            <PrimaryButton
              buttonType={PrimaryButtonType.PRIMARY_DARK}
              size={PrimaryButtonSize.MEDIUM}
              style={{ width: "154px", height: "35px" }}
              onClick={handleAddTier}>
              {t("btn_add_tier")}
            </PrimaryButton>
          )}
        </div>
      </div>

      <div className={styles.tableCard}>
        {/* The eleven fee columns can't fit any realistic viewport, so the table keeps its
            natural width and scrolls, with the tier column pinned to the start edge and the
            actions column to the end edge so both stay reachable. */}
        <div className={styles.tableScroller} dir="rtl">
          <table className={styles.matrix}>
            <colgroup>
              <col className={styles.colName} />
              <col className={styles.colBound} />
              <col className={styles.colBound} />
              {FEE_COLUMNS.map(({ field }) => (
                <col key={field} className={styles.colFee} />
              ))}
              {isEditable && <col className={styles.colActions} />}
            </colgroup>
            <thead>
              <tr>
                <th scope="col" className={styles.stickyStart}>
                  {t("lbl_tier_name")}
                </th>
                <th scope="col">{t("lbl_from")}</th>
                <th scope="col">{t("lbl_to")}</th>
                {FEE_COLUMNS.map(({ field, labelKey }) => (
                  <th scope="col" key={field}>
                    {t(labelKey)}
                  </th>
                ))}
                {isEditable && (
                  <th scope="col" className={styles.stickyEnd}>
                    {t("lbl_actions")}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {tiers.length === 0 && (
                <tr>
                  <td
                    className={styles.emptyRow}
                    colSpan={FEE_COLUMNS.length + (isEditable ? 4 : 3)}>
                    {t("lbl_no_tiers_yet")}
                  </td>
                </tr>
              )}
              {tiers.map((row, rowIndex) => {
                const isEditing = expandedRow === rowIndex;

                return (
                  <tr
                    key={row.id ?? `draft-${rowIndex}`}
                    className={classNames({
                      [styles.rowEditing]: isEditing,
                    })}>
                    <th scope="row" className={styles.stickyStart}>
                      <span className={styles.tierName}>
                        {row.name?.trim() || t("lbl_new_tier")}
                      </span>
                    </th>
                    <td>{renderBound(row.from)}</td>
                    <td>{renderBound(row.to, true)}</td>
                    {FEE_COLUMNS.map(({ field }) => (
                      <td key={field}>
                        {renderFeeValue(row[field] as string)}
                      </td>
                    ))}
                    {isEditable && (
                      <td className={styles.stickyEnd}>
                        <div className={styles.actionsContainer}>
                          <button
                            type="button"
                            className={classNames(
                              styles.actionText,
                              styles.editText
                            )}
                            onClick={() => handleToggleExpand(rowIndex)}>
                            {isEditing
                              ? t("btn_done_per_row")
                              : t("btn_edit_per_row")}
                          </button>
                          <button
                            type="button"
                            disabled={tiers?.length <= 1}
                            className={classNames(
                              styles.actionText,
                              styles.deleteText,
                              {
                                [styles.deleteBlockedText]: tiers?.length <= 1,
                              }
                            )}
                            onClick={() => handleDeleteRow(rowIndex)}>
                            {t("btn_delete_per_row")}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {isEditable && expandedRow !== null && renderEditor(expandedRow)}
      </div>

      {isEditable && (
        <div className={styles.saveButtonWrapper}>
          <PrimaryButton
            buttonType={PrimaryButtonType.PRIMARY_DARK}
            size={PrimaryButtonSize.MEDIUM}
            width="200px"
            disabled={!isDirty}
            onClick={handleSave}>
            {t("btn_save_changes")}
          </PrimaryButton>
          {secondaryAction}
        </div>
      )}
    </div>
  );
};

export default TiersInformation;
